import React, { useState, useEffect, useRef, useCallback } from "react";

const ChatInput = React.memo(({ onSend, onStop, loading, t, isSubChat, passCash, model, isRegeneratingQuestions }) => {
    const [inputText, setInputText] = useState("");
    const [isListening, setIsListening] = useState(false);
    const recognitionRef = useRef(null);

    const toggleSpeech = useCallback(() => {
        const SpeechRecognition = window.SpeechRecognition || window.webkitSpeechRecognition;
        if (!SpeechRecognition) {
            alert(t("Pages.Chatbot.SpeechNotSupported") || "ميزة التعرف على الصوت غير مدعومة في متصفحك. يرجى استخدام Chrome أو Edge.");
            return;
        }

        if (recognitionRef.current) {
            recognitionRef.current.stop();
            setIsListening(false);
            recognitionRef.current = null;
            return;
        }

        const recognition = new SpeechRecognition();
        recognition.lang = "ar-EG";
        recognition.interimResults = false;
        recognition.maxAlternatives = 1;

        recognition.onstart = () => {
            setIsListening(true);
        };

        recognition.onresult = (event) => {
            const speechToText = event.results[0][0].transcript;
            setInputText((prev) => (prev + " " + speechToText).trim());
        };

        recognition.onerror = () => {
            setIsListening(false);
            recognitionRef.current = null;
        };

        recognition.onend = () => {
            setIsListening(false);
            recognitionRef.current = null;
        };

        recognitionRef.current = recognition;
        recognition.start();
    }, [t]);

    useEffect(() => {
        return () => {
            if (recognitionRef.current) {
                recognitionRef.current.stop();
            }
        };
    }, []);

    const hasNonArabicLetters = useCallback((text) => {
        const letters = text.match(/\p{L}/gu);
        if (!letters) return false;
        return letters.some(char => {
            const code = char.charCodeAt(0);
            const isArabicRange = (code >= 0x0600 && code <= 0x06FF) ||
                (code >= 0x0750 && code <= 0x077F) ||
                (code >= 0x08A0 && code <= 0x08FF) ||
                (code >= 0xFB50 && code <= 0xFDFF) ||
                (code >= 0xFE70 && code <= 0xFEFF);
            return !isArabicRange;
        });
    }, []);

    const handleSend = useCallback((e) => {
        if (e) e.preventDefault();
        if (inputText.trim().length >= 3 && !hasNonArabicLetters(inputText) && !loading) {
            onSend(inputText.trim(), passCash, model);
            setInputText("");
        }
    }, [inputText, loading, passCash, model, onSend, hasNonArabicLetters]);

    const handleKeyPress = useCallback((e) => {
        if (e.key === "Enter" && !e.shiftKey) {
            e.preventDefault();
            handleSend();
        }
    }, [handleSend]);

    const isInputShort = inputText.length > 0 && inputText.length < 3;
    const isNonArabic = hasNonArabicLetters(inputText);

    return (
        <form onSubmit={handleSend}>
            <div className={`input-group-bar ${isListening ? "input-group-bar-listening" : ""}`}>
                {/* Mic Group Button (right side in RTL) */}
                <button
                    type="button"
                    className={`mic-group-btn ${isListening ? "mic-group-btn-listening" : ""}`}
                    onClick={toggleSpeech}
                    title={isListening ? (t("Pages.Chatbot.StopListening") || "إيقاف الاستماع") : (t("Pages.Chatbot.SpeakToType") || "تحدث للكتابة")}
                    disabled={loading || isRegeneratingQuestions}
                >
                    <i className={isListening
                        ? "fas fa-microphone animate__animated animate__pulse animate__infinite"
                        : "fal fa-microphone"
                    }></i>
                    <span className="mic-group-label">
                        {isListening ? (t("Pages.Chatbot.Listening") || "جاري الاستماع...") : (t("Pages.Chatbot.VoiceLabel") || "صوت")}
                    </span>
                </button>

                {/* Divider */}
                <div className="input-group-divider"></div>

                {/* Textarea */}
                <textarea
                    value={inputText}
                    onChange={(e) => setInputText(e.target.value)}
                    onKeyDown={handleKeyPress}
                    placeholder={isSubChat
                        ? (t("Pages.Chatbot.SubChatHint") || "يمكنك سؤالي عن الدرس الحالي فقط")
                        : (t("Pages.Chatbot.Placeholder") || "اكتب رسالتك هنا...")
                    }
                    className="input-textarea"
                    disabled={loading || isRegeneratingQuestions}
                    rows={1}
                />

                {/* Send / Stop Button */}
                {loading && !isRegeneratingQuestions ? (
                    <button
                        type="button"
                        className="send-group-btn stop-group-btn animate__animated animate__pulse animate__infinite"
                        onClick={onStop}
                        title={t("Pages.Chatbot.Stop")}
                    >
                        <i className="fal fa-stop-circle"></i>
                    </button>
                ) : (
                    <button
                        type="submit"
                        className="send-group-btn"
                        disabled={inputText.trim().length < 3 || isNonArabic || isRegeneratingQuestions}
                        title={t("Pages.Chatbot.Send")}
                    >
                        <i className="fal fa-paper-plane"></i>
                    </button>
                )}
            </div>

            {isNonArabic ? (
                <div className="warning-message alert alert-warning p-2 mt-2 d-flex align-items-center small">
                    <i className="fal fa-exclamation-triangle ml-2"></i>
                    <span>{t("Pages.Chatbot.ArabicOnlyError")}</span>
                </div>
            ) : isInputShort ? (
                <div className="warning-message alert alert-warning p-2 mt-2 d-flex align-items-center small">
                    <i className="fal fa-exclamation-triangle ml-2"></i>
                    <span>{t("Pages.Chatbot.MinLettersError")}</span>
                </div>
            ) : null}
        </form>
    );
});

export default ChatInput;
